import { EcAttributes } from "~/models/ec-attribute";
import { AnalysisLayerAPI, ZoneAPI, IAnalysisLayer, LayerAPI } from "@ai360/core";
import { ICropDropdownOption, isCropUnlinked } from "~/admin/setup/customer/use-crop-list";

//these are duplicated in client.core's layer-utils and any changes may need to be duplicated there
export const ANALYSIS_INFO_NAME_IMAGERY_SETUP_PLANET = "Imagery Setup - Planet";
export const ANALYSIS_INFO_NAME_IMAGERY_SETUP_SENTINEL = "Imagery Setup - Sentinel 2";

export const ANALYSIS_INFO_NAME_FOUR_MATION = "4Mation";
export const ANALYSIS_INFO_NAME_MANAGEMENT_AREA = "Management Area";
export const ANALYSIS_INFO_NAME_NORMALIZED_YIELD = "Normalized Yield";
export const ANALYSIS_INFO_NAME_NORMALIZED_YIELD_BENCHMARK = "Normalized Yield - Benchmark";
export const ANALYSIS_INFO_NAME_PROFIT_LOSS = "Profit/Loss";
export const ANALYSIS_INFO_NAME_SEED_STRONG = "SeedStrong";
export const ANALYSIS_INFO_NAME_SEED_STRONG_DH = "SeedStrong DH";
export const ANALYSIS_INFO_NAME_EC_DATA = "Multi-Field EC Data";
export const MANAGEMENT_AREA_TEMPLATE_CROP_ZONE = "Crop Zone";
export const ANALYSIS_INFO_TYPE_GUID = "analysisLayerTypeGuid";
export const ANALYSIS_INFO_TYPE_NAME = "name";
export const ANALYSIS_INFO_NAME_UNKNOWN = "UNKNOWN";
export const ANALYSIS_ERROR_CODES_LIST = [2800, 2806, 2836, 2835, 2903, 2911, 3008, 3009];

export interface IAnalysisLayerArea extends ZoneAPI.IZone {
    analysisLayerAreaGuid: string;
    analysisLayerAreaId: number;
    analysisLayerGuid: string;
    fieldBoundaryGuid: string;
    applyAnalysisToArea: boolean;
    calculatedArea: number;
}

export interface IAnalysisLayerSummary {
    analysisLayerAreaList: AnalysisLayerAPI.IAnalysisLayerZone[];
    batchGuid: string;
    isNew: boolean;
    layers: IAnalysisLayer[];
    name: string;
    numClasses: string;
    typeGuid: string;
    updateBatchGuid?: boolean;
    editAnalysisLayerGuid?: string;
}

export interface IAnalysisLayerSummaryItem {
    analysisLayerGuid: string;
    analysisLayerTypeGuid: string;
    analysisLayerTypeName: string;
    batchGuid: string;
    fieldBoundaryGuid: string;
    fieldGuid: string;
    name: string;
    numClasses: number;
    status: string;
}

export interface IAnalaysisManagementArea extends IAnalysisLayerSummary {
    croppingSeasonGuid: string;
    isMultipartZoneAutoSplitAllowed: () => boolean;
    type: typeof ANALYSIS_INFO_NAME_MANAGEMENT_AREA;
    managementAreaTemplateGuid: string;
    managementAreaData: Map<number, ManagementAreaAttribute[]>;
}

export interface IAnalysisMultiFieldEcDataSummary extends IAnalysisLayerSummary {
    type: typeof ANALYSIS_INFO_NAME_EC_DATA;
    classificationMethod: string;
    attribute: number;
    colorRampGuid: string;
}

export interface IAnalysisNormaliedYieldSummary extends IAnalysisLayerSummary {
    type:
        | typeof ANALYSIS_INFO_NAME_NORMALIZED_YIELD
        | typeof ANALYSIS_INFO_NAME_NORMALIZED_YIELD_BENCHMARK;
    agEventGeneralGuidList: string[];
    cropList: LayerAPI.INormalizedYieldCrop[];
    croppingSeasonNameList?: number[];
    isMultipartZoneAutoSplitAllowed: () => boolean;
}

export interface IAnalysisImagerySetupSummary extends IAnalysisLayerSummary {
    agEventGeneralGuidList: string[];
    analysisLayerGuid: string;
    analysisLayerType:
        | typeof ANALYSIS_INFO_NAME_IMAGERY_SETUP_PLANET
        | typeof ANALYSIS_INFO_NAME_IMAGERY_SETUP_SENTINEL;
    analysisLayerTypeGuid: string;
    calculateAbsoluteNdvi: boolean;
    croppingSeasonGuid: string;
    endMonth: number;
    hasChanged: boolean;
    imageryTypeGuidList: string[];
    maximumPercentCloudsAllowed: number;
    name: string;
    numClasses: string;
    startMonth: number;
    type:
        | typeof ANALYSIS_INFO_NAME_IMAGERY_SETUP_PLANET
        | typeof ANALYSIS_INFO_NAME_IMAGERY_SETUP_SENTINEL;
    userGuid: string;
    zonesAdded: boolean;
}

export interface IAnalysisProfitLossSummary extends IAnalysisLayerSummary {
    type: typeof ANALYSIS_INFO_NAME_PROFIT_LOSS;
    cropGuid: string;
    sellingPrice: number;
    cost: ProfitLossCost;
    croppingSeasonGuid: string;
    detailedTypeCostInfo?: ProfitLossDetailedCostInfo;
    agEventGeneralGuidList: string[];
    eventSelectionUsed: boolean;
}

export interface IAnalysisSeedStrongSummary extends IAnalysisLayerSummary {
    type:
        | typeof ANALYSIS_INFO_NAME_SEED_STRONG
        | typeof ANALYSIS_INFO_NAME_SEED_STRONG_DH
        | typeof ANALYSIS_INFO_NAME_FOUR_MATION;
    normalizedYieldGuid: string;
    useEcData: boolean;
    isSeedStrongDH: boolean;
    is4Mation: boolean;
    usePlantingEvent: boolean;
}

export enum ProfitLossCostType {
    CostPerAcre = 0,
    BreakEvenYield = 1,
    Detailed = 2,
}

export interface ProfitLossCost {
    type: ProfitLossCostType;
    value?: number;
}

export interface ProfitLossActualCost {
    croppingSeasonGuid: string;
    fieldGuids: string[];
}

export interface ProfitLossDetailedCostInfo {
    seed: number;
    fertilizer: number;
    chemical: number;
    irrigation: number;
    harvest: number;
    equipment: number;
    labor: number;
    cropInsurance: number;
    interestOperatingCapital: number;
    miscellaneous: number;
    land: number;
    nonCropIncome: number;
    total: number;
    useSeed: boolean;
    useFertilizer: boolean;
    useChemical: boolean;
}

export interface ManagementAreaAttribute {
    importAttributeGuid: string;
    columnOrder: number;
    dataType: string;
    name: string;
    textValue?: string;
    yesNoValue?: boolean;
    decimalIntValue?: number;
}

export type AnyAnalysisSummary =
    | IAnalaysisManagementArea
    | IAnalysisMultiFieldEcDataSummary
    | IAnalysisNormaliedYieldSummary
    | IAnalysisProfitLossSummary
    | IAnalysisSeedStrongSummary
    | IAnalysisImagerySetupSummary;

// Mirrors analysis-management-area-form.tsx's own cropAttributes set -- not imported from there to
// avoid a model->component circular dependency; the two "Crop"/"Crop 2" attribute names are stable.
const MANAGEMENT_AREA_CROP_ATTRIBUTE_NAMES = new Set(["Crop", "Crop 2"]);

// Management Area stores a crop attribute's value by crop *name*, not guid, so the shared
// isCropUnlinked(guid, crops) utility doesn't apply here.
const isCropNameUnlinked = (name: string, cropData: ICropDropdownOption[]): boolean => {
    const crop = cropData.find((c) => c.name === name);
    return crop != null && crop.isAgvanceLinked === false;
};

export const areAllRequiredFieldsSet = (
    summary: AnyAnalysisSummary,
    cropData: ICropDropdownOption[]
): boolean => {
    switch (summary.type) {
        case ANALYSIS_INFO_NAME_NORMALIZED_YIELD:
            return (
                summary.agEventGeneralGuidList &&
                summary.agEventGeneralGuidList.length > 0 &&
                summary.layers.length > 0 &&
                summary.name !== ""
            );
        case ANALYSIS_INFO_NAME_NORMALIZED_YIELD_BENCHMARK:
            return (
                summary.cropList &&
                summary.cropList.length > 0 &&
                summary.cropList.every(
                    (c) => c.benchmarkYield !== null && c.benchmarkYield !== 0
                ) &&
                summary.layers.length > 0 &&
                summary.name !== ""
            );
        case ANALYSIS_INFO_NAME_SEED_STRONG:
        case ANALYSIS_INFO_NAME_SEED_STRONG_DH:
        case ANALYSIS_INFO_NAME_FOUR_MATION:
            return (
                summary.normalizedYieldGuid !== "" &&
                summary.layers.length > 0 &&
                summary.name !== ""
            );
        case ANALYSIS_INFO_NAME_PROFIT_LOSS:
            return (
                summary.layers.length > 0 &&
                summary.name != null &&
                summary.name !== "" &&
                summary.croppingSeasonGuid != null &&
                summary.croppingSeasonGuid !== "" &&
                summary.cropGuid != null &&
                summary.cropGuid !== "" &&
                summary.sellingPrice != null &&
                summary.sellingPrice > 0 &&
                summary.cost.value != null &&
                summary.cost.value > 0 &&
                !isCropUnlinked(summary.cropGuid, cropData)
            );
        case ANALYSIS_INFO_NAME_EC_DATA:
            return (
                summary.layers.length > 0 &&
                summary.name !== "" &&
                EcAttributes.all()
                    .map((x) => x.attributeId)
                    .includes(summary.attribute) &&
                summary.numClasses != null &&
                summary.colorRampGuid != null &&
                summary.classificationMethod != null
            );
        case ANALYSIS_INFO_NAME_MANAGEMENT_AREA: {
            const managementAreaAttributes = Object.values(summary.managementAreaData).flat();
            return (
                summary.layers.length > 0 &&
                summary.name !== "" &&
                managementAreaAttributes.some(
                    (a) => a.textValue != null || a.yesNoValue === true || a.decimalIntValue != null
                ) &&
                !managementAreaAttributes.some(
                    (a) =>
                        MANAGEMENT_AREA_CROP_ATTRIBUTE_NAMES.has(a.name) &&
                        a.textValue != null &&
                        isCropNameUnlinked(a.textValue, cropData)
                )
            );
        }
        case ANALYSIS_INFO_NAME_IMAGERY_SETUP_PLANET:
        case ANALYSIS_INFO_NAME_IMAGERY_SETUP_SENTINEL:
            return (
                summary.name !== "" &&
                summary.croppingSeasonGuid != null &&
                summary.imageryTypeGuidList &&
                summary.imageryTypeGuidList.length > 0 &&
                summary.endMonth != null &&
                summary.startMonth != null &&
                summary.maximumPercentCloudsAllowed != null &&
                summary.maximumPercentCloudsAllowed < 101 &&
                summary.hasChanged
            );
    }
};
